/**
 * useCapabilities hook — fetches capabilities from GET /auth/capabilities.
 *
 * Triggers re-fetch on:
 *   - Login (token appears)
 *   - Impersonation switch (org change)
 *   - Token refresh (new access token)
 * Resets to viewer-level on logout (token cleared).
 */
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { useImpersonationStore } from '@/store/impersonation'
import { useCapabilitiesStore, type Capabilities } from '@/store/capabilities'

export function useCapabilities(): Capabilities {
  const location = useLocation()
  const token = useAuthStore((state) => state.token)
  const impersonatedOrgId = useImpersonationStore((state) => state.impersonatedOrgId)
  const capabilities = useCapabilitiesStore((state) => state.capabilities)
  const fetchCapabilities = useCapabilitiesStore((state) => state.fetchCapabilities)
  const resetCapabilities = useCapabilitiesStore((state) => state.resetCapabilities)
  const fetchKey = `${token ?? ''}:${impersonatedOrgId ?? ''}`

  useEffect(() => {
    if (token) {
      void fetchCapabilities(fetchKey)
    } else {
      resetCapabilities()
    }
  }, [token, fetchKey, fetchCapabilities, resetCapabilities])

  useEffect(() => {
    if (!token || !location.pathname.startsWith('/admin')) {
      return
    }

    void fetchCapabilities(fetchKey, true)

    const revalidate = () => {
      if (document.visibilityState === 'visible') {
        void fetchCapabilities(fetchKey, true)
      }
    }

    window.addEventListener('focus', revalidate)
    document.addEventListener('visibilitychange', revalidate)
    return () => {
      window.removeEventListener('focus', revalidate)
      document.removeEventListener('visibilitychange', revalidate)
    }
  }, [token, fetchKey, fetchCapabilities, location.pathname])

  return capabilities
}

/**
 * Check if user has admin access (superadmin or org_admin).
 * Convenience wrapper for route guards.
 */
export function useIsAdmin(): boolean {
  const { adminAccess } = useCapabilities()
  return adminAccess
}

/**
 * Check if user is superadmin.
 * Convenience wrapper for superadmin-only features.
 */
export function useIsSuperadmin(): boolean {
  const { superadminAccess } = useCapabilities()
  return superadminAccess
}
